import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from '@reach/router';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { setSchoolDetails } from 'reducers/misc';
import { useFetchSchoolInfo } from 'hooks/useFetchSchoolInfo';
import { onlyDigitsBracketsSpacesDashesPluses } from 'utils/regex';
import { SubscribeApi } from 'api/subscribeApi';
import { ISchoolDetails, SubscribeParser } from 'pages/subscribe/models';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import FormTextField from 'components/formComponents/FormTextField';
import { AppPath } from 'appConstants';
import { Spinner } from 'components/spinner/Spinner';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';

type SchoolDetailsFormProps = {
  onSubmit: () => void;
  hideSummaryPanel?: boolean;
};

const SchoolDetailsForm: React.FC<SchoolDetailsFormProps> = ({ onSubmit, hideSummaryPanel }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const pathname = location.pathname;

  const { schoolInfo, error: schoolDetailsError } =
    pathname === AppPath.UPGRADE_SUBSCRIPTION
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useFetchSchoolInfo()
      : { schoolInfo: null, error: null };

  const [error, setError] = useState('');

  const validationSchema = Yup.object({
    schoolname: Yup.string().required('Please enter the school name'),
    phoneNumber: Yup.string()
      .required('Please enter the school phone number')
      .matches(
        onlyDigitsBracketsSpacesDashesPluses,
        'Please enter a valid phone number'
      ),
    addressLine1: Yup.string().required('Please enter the school address'),
    addressLine2: Yup.string(),
    addressLine3: Yup.string(),
    townCity: Yup.string().required('Please enter the school town/city'),
    county: Yup.string(),
    postcodeZipcode: Yup.string().required(
      'Please enter the school postcode/zipcode'
    ),
    country: Yup.string().required('Please enter the school country'),
  });

  const handleSubmitForm = async (
    values: ISchoolDetails,
    { setSubmitting }: FormikHelpers<ISchoolDetails>
  ) => {
    const result =
      pathname === AppPath.UPGRADE_SUBSCRIPTION
        ? await SubscriptionsApi.updateSchoolInfo(values)
        : await SubscribeApi.validateSchoolInfo(values);

    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
      return;
    }
    dispatch(setSchoolDetails(values));
    onSubmit();
    setSubmitting(false);
  };

  return (
    <div className="Form">
      <FormGeneralError message={error} />
      {(pathname === AppPath.UPGRADE_SUBSCRIPTION &&
        (schoolInfo || schoolDetailsError)) ||
      pathname !== AppPath.UPGRADE_SUBSCRIPTION ? (
        <Formik
          initialValues={SubscribeParser.createEmptySchoolDetails(schoolInfo)}
          onSubmit={(values, formikHelpers) =>
            handleSubmitForm(values, formikHelpers)
          }
          validationSchema={validationSchema}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <div className="PricingColRow">
                <div className="PCRowInner">
                  <div className="LeftCol">
                    <div className="SideBySide">
                      <FormTextField
                        field={'schoolname'}
                        label={'School name'}
                        required={true}
                        value={values.schoolname}
                      />
                      <FormTextField
                        field={'phoneNumber'}
                        label={'School phone number'}
                        required={true}
                        value={values.phoneNumber}
                      />
                    </div>
                    <div className="LineSpacer4"></div>
                    <h4>School Address</h4>
                    <FormTextField
                      field={'addressLine1'}
                      label={'Address line 1'}
                      required={true}
                      value={values.addressLine1}
                    />
                    <div className="SideBySide">
                      <FormTextField
                        field={'addressLine2'}
                        label={'Address line 2'}
                        required={false}
                        optionalTag={'optional'}
                        value={values.addressLine2}
                      />
                      <FormTextField
                        field={'addressLine3'}
                        label={'Address line 3'}
                        required={false}
                        optionalTag={'optional'}
                        value={values.addressLine3}
                      />
                    </div>
                    <div className="SideBySide Outer">
                      <div className="SideBySide">
                        <FormTextField
                          field={'townCity'}
                          label={'Town/City'}
                          required={true}
                          value={values.townCity}
                        />
                        <FormTextField
                          field={'county'}
                          label={'County'}
                          required={false}
                          optionalTag={'optional'}
                          value={values.county}
                        />
                      </div>
                      <div className="SideBySide">
                        <FormTextField
                          field={'postcodeZipcode'}
                          label={'Postcode / zipcode'}
                          required={true}
                          value={values.postcodeZipcode}
                        />
                        <FormTextField
                          field={'country'}
                          label={'Country'}
                          required={true}
                          value={values.country}
                        />
                      </div>               
                    </div>
                    {hideSummaryPanel &&
                      <button
                        className="Btn BtnOrange"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        Save and continue
                      </button>
                    }
                  </div>
                  {!hideSummaryPanel &&
                    <div className="RightCol">
                      <PricingSummaryDetails 
                        addPromoStep={false}
                        isDisabled={isSubmitting}
                        btnText="Save and continue"
                      />
                    </div>                  
                  }
                </div> 
              </div>                      
            </Form>
          )}
        </Formik>
      ) : (
        <div
          style={{
            minHeight: ' calc(100vh - 300px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default SchoolDetailsForm;
